/**
 * Application entry point.
 *
 * Query defaults are chosen for an internal console: a failed read is retried once (a flaky
 * network is common, a duplicated read is harmless), a failed *command* is never retried
 * automatically because the backend's idempotency key — not a client retry — is what makes a
 * repeat safe, and windows do not refetch on focus so a long-running form is not disturbed.
 */
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { App } from './App';
import { isApiError } from './api/errors';
import { SessionProvider } from './auth/session';
import { ErrorBoundary } from './shell/ErrorBoundary';
import './styles/tokens.css';
import './styles/app.css';
/*
 * The stylesheet the visual acceptance actually measures.
 *
 * `styles/app.css` is the Phase-4 port of the same frozen `<style>` block and it still dresses the
 * application shell (the error boundary, the session states, the sign-in screen). Every SCREEN is
 * now the prototype's, and the prototype's screens have to be painted by the copy the parity
 * harness compares against the reference — otherwise the product would be measured on one
 * stylesheet and served on another, which is precisely the same-source trap §4 exists to prevent.
 * It is imported last so that wherever the two copies state the same rule, the measured one wins.
 */
import './v07/styles/fonts.css';
import './v07/styles/prototype.css';
import './v07/styles/approved-corrections.css';
import './v07/styles/certificate-email.css';
/*
 * The register's own approved reference (15 October 2026), scoped to that one screen.
 *
 * Imported LAST and kept out of `v07/styles/` on purpose: the demo bundle — the surface the visual
 * acceptance gate measures against the frozen prototype — imports `v07/styles/*` only, so this file
 * never reaches it. The gate therefore still reads the frozen design, and the product follows the
 * reference the Product Owner approved for this screen. See the file's own header.
 */
import './styles/credentials-register.css';
/*
 * The Panel's own approved reference (15 October 2026), scoped to that one screen, and kept out of
 * `v07/styles/` for the same reason as the register's: the demo bundle never loads it, so the
 * frozen visual gate keeps reading the frozen design while the product follows the reference.
 */
import './styles/dashboard-panel.css';
import { ToastProvider } from './ui';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (attempt, error) => {
        // An authorization, validation or conflict answer is the backend's decision: retrying
        // it just repeats the same refusal.
        if (isApiError(error) && error.status >= 400 && error.status < 500) return false;
        return attempt < 1;
      },
      refetchOnWindowFocus: false,
      staleTime: 15_000,
    },
    mutations: { retry: false },
  },
});

const container = document.getElementById('root');
if (!container) throw new Error('#root bulunamadı');

createRoot(container).render(
  <StrictMode>
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <SessionProvider>
            <ToastProvider>
              <App />
            </ToastProvider>
          </SessionProvider>
        </BrowserRouter>
      </QueryClientProvider>
    </ErrorBoundary>
  </StrictMode>,
);
